/**
 * BillingSuccess — Phase 7.UI.15.
 *
 * Public success route shown after the user returns from CopeCart
 * checkout. CRITICAL: this page NEVER activates a plan from query
 * params alone — that would let anyone craft a malicious URL. The
 * authoritative source of truth is the webhook → /api/billing/status
 * round-trip.
 *
 * UX:
 *   1. Read intended plan/interval from query params (display only).
 *   2. Poll GET /api/billing/status every 3s for up to 90s.
 *   3. When `plan` matches and `subscription_status === 'active'`,
 *      show the activated state + CTA to /analyze and /billing.
 *   4. Otherwise show a calm pending state — webhook may be in flight.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function BillingSuccess() {
  const q = useQuery();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const expectedPlan = (q.get("plan") || "").toLowerCase();
  const expectedInterval = (q.get("interval") || "monthly").toLowerCase();
  const [billing, setBilling] = useState(null);
  const [polling, setPolling] = useState(true);
  const [elapsedS, setElapsedS] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let startedAt = Date.now();
    if (!user) {
      setPolling(false);
      return;
    }
    const poll = async () => {
      try {
        const res = await api.get("/billing/status");
        if (cancelled) return;
        setBilling(res.data);
        const planMatch = expectedPlan ? res.data?.plan === expectedPlan : (res.data?.plan === "pro" || res.data?.plan === "premium");
        const active = res.data?.subscription_status === "active";
        if (planMatch && active) {
          setPolling(false);
          return;
        }
      } catch {
        // Ignore transient errors during polling.
      }
      const elapsed = (Date.now() - startedAt) / 1000;
      setElapsedS(elapsed);
      if (elapsed > 90) {
        setPolling(false);
        return;
      }
      if (!cancelled) setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const planActive = billing?.subscription_status === "active" &&
    (expectedPlan ? billing?.plan === expectedPlan : ["pro", "premium"].includes(billing?.plan));

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main className="flex-1 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="billing-success-page">
        <div className="rounded-2xl border border-border/70 bg-card/70 p-6 sm:p-8 shadow-[var(--app-shadow-elev-1)]">
          {planActive ? (
            <div className="flex flex-col items-start gap-3" data-testid="billing-success-active">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                <CheckCircle2 className="w-5 h-5" />
              </span>
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight" data-testid="billing-success-title-active">
                You're on Stockaily {billing.plan === "premium" ? "Premium" : "Pro"}.
              </h1>
              <p className="text-muted-foreground">
                Your payment is confirmed and your account has been upgraded.
                {billing.access_until ? (
                  <span className="block text-[12px] mt-1">
                    Access until <span className="font-mono">{billing.access_until.slice(0, 10)}</span>
                    {billing.billing_interval ? ` · ${billing.billing_interval}` : ""}
                  </span>
                ) : null}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => navigate("/")} data-testid="billing-success-cta-home">
                  Go to Stockaily
                </Button>
                <Button variant="outline" onClick={() => navigate("/billing")} data-testid="billing-success-cta-billing">
                  Open account settings
                </Button>
              </div>
              {/* Phase 7.UI.15b — CopeCart compliance copy */}
              <div
                className="mt-4 text-[11.5px] text-muted-foreground/80"
                data-testid="billing-success-copecart-notice-active"
              >
                {t("billing.success_charged_by_copecart")}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3" data-testid="billing-success-pending">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[#3182CE]/15 text-[#3182CE]">
                {polling ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              </span>
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight">
                Payment received. We're activating your Stockaily plan.
              </h1>
              <p className="text-muted-foreground">
                {polling
                  ? "This usually takes a few seconds. We're confirming the webhook from CopeCart."
                  : "Your activation is taking longer than usual. If your plan does not appear within a few minutes, please contact support."}
                {expectedPlan ? (
                  <span className="block text-[12px] mt-1">
                    Expected: <span className="font-mono">{expectedPlan}</span> · {expectedInterval}
                  </span>
                ) : null}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => navigate("/")} data-testid="billing-success-cta-home-pending">
                  Go to Stockaily
                </Button>
                <Link to="/billing" className="inline-flex items-center text-[12px] text-muted-foreground hover:text-foreground" data-testid="billing-success-link-billing">
                  Open account settings
                </Link>
              </div>
              {polling ? (
                <div className="text-[10.5px] text-muted-foreground/70 mt-3" data-testid="billing-success-elapsed">
                  Checking… {Math.round(elapsedS)}s
                </div>
              ) : null}
              {/* Phase 7.UI.15b — CopeCart compliance copy */}
              <div
                className="mt-4 text-[11.5px] text-muted-foreground/80"
                data-testid="billing-success-copecart-notice-pending"
              >
                {t("billing.success_charged_by_copecart")}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
